/**
 * StartProjectDialog — vanuit een gesprek een project of lead starten.
 *
 * De volgorde is bewust: eerst lezen wat er in het gesprek staat, dan kijken wat Gripp
 * al van deze klant weet, en pas dan aanmaken. Zo voorkom je het meest voorkomende
 * ongeluk — een tweede project naast een offerte die er al ligt.
 *
 * Kiest de gebruiker voor een lege kans in Gripp, dan krijgt het project meteen een
 * GrippID. Wordt die kans later een echte offerte, dan herkent de offerte-sync het
 * bestaande project op dat ID en vult hem aan in plaats van te dupliceren.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import toast from 'react-hot-toast';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';

const vertrouwenKleur = { hoog: 'success', gemiddeld: 'warning', laag: 'default' };

export default function StartProjectDialog({ open, onClose, meeting, clients, onCreated }) {
  const [laden, setLaden] = useState(false);
  const [voorstel, setVoorstel] = useState(null);
  const [fout, setFout] = useState(null);

  const [naam, setNaam] = useState('');
  const [klant, setKlant] = useState(null);
  const [omschrijving, setOmschrijving] = useState('');
  const [notitie, setNotitie] = useState('');

  const [grippLaden, setGrippLaden] = useState(false);
  const [gripp, setGripp] = useState(null);
  const [grippBedrijf, setGrippBedrijf] = useState(null);
  const [maakKans, setMaakKans] = useState(true);
  const [aanmaken, setAanmaken] = useState(false);

  // 1. Het gesprek laten lezen zodra de dialoog opengaat.
  useEffect(() => {
    if (!open || !meeting?.id) return;
    let afgebroken = false;
    setLaden(true); setFout(null); setVoorstel(null);
    (async () => {
      try {
        const r = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}/project-proposal`, { method: 'POST' });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
        const d = await r.json();
        if (afgebroken) return;
        setVoorstel(d);
        setNaam(d.projectnaam || meeting.title || '');
        setOmschrijving(d.omschrijving || '');
        setNotitie(d.notitie || '');
        if (d.klant) {
          const treffer = (clients || []).find((c) => c.label?.toLowerCase().includes(d.klant.toLowerCase()));
          if (treffer) setKlant(treffer);
        }
      } catch (e) {
        if (!afgebroken) { setFout(e.message); setNaam(meeting.title || ''); }
      } finally {
        if (!afgebroken) setLaden(false);
      }
    })();
    return () => { afgebroken = true; };
  }, [open, meeting?.id, meeting?.title, clients]);

  // 2. Wat weet Gripp al van deze klant?
  const checkGripp = useCallback(async (bedrijfsnaam) => {
    if (!bedrijfsnaam || bedrijfsnaam.length < 2) return;
    setGrippLaden(true); setGripp(null); setGrippBedrijf(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/projects/gripp-context?bedrijf=${encodeURIComponent(bedrijfsnaam)}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setGripp(d);
      if (d.bedrijven?.length === 1) setGrippBedrijf(d.bedrijven[0]);
      // Ligt er al iets open, dan is een extra kans meestal juist verwarrend.
      const heeftOpen = (d.offertes || []).some((o) => !o.acceptatie);
      setMaakKans(!heeftOpen && d.bedrijven?.length === 1);
    } catch (e) {
      setGripp({ gevonden: false, fout: e.message, bedrijven: [], offertes: [] });
    } finally {
      setGrippLaden(false);
    }
  }, []);

  useEffect(() => {
    const bedrijfsnaam = klant?.label || voorstel?.klant;
    if (open && bedrijfsnaam) checkGripp(bedrijfsnaam);
  }, [open, klant?.label, voorstel?.klant, checkGripp]);

  const aanmakenNu = async () => {
    if (!naam.trim()) { toast.error('Projectnaam is verplicht'); return; }
    setAanmaken(true);
    try {
      const body = {
        meetingId: meeting.id,
        projectnaam: naam.trim(),
        omschrijving: omschrijving || null,
        notitie: notitie || null,
        maakKans: !!(maakKans && grippBedrijf),
        grippCompanyId: grippBedrijf?.id || null,
      };
      if (klant?.id) body.klantnaam = [klant.id];

      const r = await authFetch(`${BACKEND_URL}/Api/projects/from-meeting`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error?.message || `HTTP ${r.status}`);

      const waarschuwingen = Object.entries(d.stappen || {})
        .filter(([, v]) => v && v.fout)
        .map(([k]) => k);
      if (waarschuwingen.length) {
        toast(`Project aangemaakt, maar dit lukte niet: ${waarschuwingen.join(', ')}`, { icon: '⚠️' });
      } else {
        toast.success(d.kansNummer ? `Project aangemaakt met kans ${d.kansNummer} in Gripp` : 'Project aangemaakt');
      }
      onCreated?.(d.project);
      onClose();
    } catch (e) {
      toast.error(`Aanmaken mislukt: ${e.message}`);
    } finally {
      setAanmaken(false);
    }
  };

  const openOffertes = (gripp?.offertes || []).filter((o) => !o.acceptatie);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 20, color: '#7c3aed' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Project starten vanuit dit gesprek</Typography>
          {voorstel?.vertrouwen && (
            <Tooltip title="Hoe concreet dit gesprek over een opdracht ging">
              <Chip size="small" label={voorstel.vertrouwen} color={vertrouwenKleur[voorstel.vertrouwen] || 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
            </Tooltip>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {laden && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Het gesprek wordt gelezen…</Typography>
          </Box>
        )}

        {fout && <Alert severity="warning" sx={{ mb: 2 }}>Voorstel maken lukte niet ({fout}). Je kunt de velden zelf invullen.</Alert>}

        {!laden && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Projectnaam" value={naam} onChange={(e) => setNaam(e.target.value)} size="small" fullWidth autoFocus />

            <Autocomplete
              options={clients || []} value={klant} onChange={(_, v) => setKlant(v)} size="small"
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Klant" helperText={voorstel?.klant ? `Uit het gesprek: ${voorstel.klant}` : ' '} />}
            />

            <TextField label="Omschrijving" value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} size="small" fullWidth multiline rows={3} />

            {(voorstel?.deliverables?.length > 0 || voorstel?.deadline_signaal || voorstel?.budget_signaal) && (
              <Box>
                <Typography variant="caption" color="text.secondary">Genoemd in het gesprek</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {(voorstel.deliverables || []).map((d, i) => <Chip key={i} label={d} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />)}
                </Box>
                {voorstel.deadline_signaal && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>🗓️ {voorstel.deadline_signaal}</Typography>}
                {voorstel.budget_signaal && <Typography variant="caption" display="block">💶 {voorstel.budget_signaal}</Typography>}
              </Box>
            )}

            <TextField label="Notitie bij het project" value={notitie} onChange={(e) => setNotitie(e.target.value)} size="small" fullWidth multiline rows={4} />

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Gripp</Typography>
              {grippLaden && <Typography variant="caption" color="text.secondary">Zoeken in Gripp…</Typography>}

              {!grippLaden && gripp && !gripp.gevonden && (
                <Alert severity="info" sx={{ py: 0.5 }}>Geen bedrijf gevonden in Gripp voor “{gripp.zoekterm}”. Zonder bedrijf kan er geen kans worden aangemaakt.</Alert>
              )}

              {!grippLaden && gripp?.bedrijven?.length > 1 && (
                <Autocomplete
                  options={gripp.bedrijven} value={grippBedrijf} onChange={(_, v) => setGrippBedrijf(v)} size="small"
                  getOptionLabel={(o) => o.naam || ''} isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => <TextField {...params} label="Welk bedrijf in Gripp?" />}
                  sx={{ mt: 1 }}
                />
              )}

              {!grippLaden && openOffertes.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                  Er staat al iets open voor deze klant:
                  {openOffertes.slice(0, 4).map((o) => (
                    <Typography key={o.id} variant="caption" display="block">
                      {o.isKans ? 'Kans' : 'Offerte'} {o.nummer} — {o.naam} {o.fase ? `(${o.fase})` : ''}
                    </Typography>
                  ))}
                  Overweeg daaraan te koppelen in plaats van iets nieuws te starten.
                </Alert>
              )}

              {!grippLaden && grippBedrijf && (
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={<Checkbox size="small" checked={maakKans} onChange={(e) => setMaakKans(e.target.checked)} />}
                  label={<Typography variant="body2">Lege kans aanmaken in Gripp ({grippBedrijf.naam}) — later om te zetten naar een offerte</Typography>}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={aanmaken}>Annuleren</Button>
        <Button
          variant="contained" onClick={aanmakenNu} disabled={aanmaken || laden || !naam.trim()}
          startIcon={aanmaken ? <CircularProgress size={14} /> : null}
          sx={{ backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6d28d9' } }}
        >
          {maakKans && grippBedrijf ? 'Aanmaken met kans' : 'Aanmaken'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
